import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Typography, Box, makeStyles, Grid } from 'transfix-ui/core'
import { IAs2ConnectionConfigConnection, IUpsertAs2ConnectionConfigInput } from 'types/graphqlTypes'
import { Spinner, Card, Button, ToastVariation, useToastState, Toast } from 'transfix-ui'
import { CONNECTION_CONFIG_TYPES } from '../ConnectionConfigTypes'
import { AS2ConnectionConfigTable } from './AS2ConnectionConfigTable'
import { AS2ConnectionConfigDetails } from './ASConnectionConfigDetails'
import { useAs2ConnectionConfigsQuery } from './graphql/AS2ConnectionConfigs'
import { useAs2ConnectionConfig } from './hooks/useAs2ConnectionConfig'
import { As2ConnectionConfigModal } from './modals/As2ConnectionConfigModal'
import { IAs2ConnectionConfigSchema } from './schema/AS2ConnectionConfigSchema'

const useAS2ConnectionConfigStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    maxWidth: '100vw',
    display: 'flex',
    overflowX: 'clip',
  },
  card: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: 'none',
    boxShadow: 'none',
  },
  text: {
    marginLeft: theme.spacing(1),
  },
}))

export const AS2ConnectionConfigs = () => {
  const { id } = useParams<{ id: string }>()
  const [isSidebarOpen, setas2ConfigSidebarOpen] = useState(!!id)
  const [as2ConnectionConfigs, setAs2ConnectionConfigs] = useState<IAs2ConnectionConfigConnection>()
  const history = useHistory()
  const [isOpen, setOpenModal] = useState<boolean>()
  const closeHandler = () => setOpenModal(false)

  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()

  const displayUserFeedback = (message: string, level: 'error' | 'info'): void => {
    const toastType: ToastVariation = level === 'error' ? 'alert' : 'default'

    openToast({
      toastType,
      toastMessage: message,
    })
  }

  const { data, loading } = useAs2ConnectionConfigsQuery({
    notifyOnNetworkStatusChange: true,
  })

  const clickHandler = () => {
    setOpenModal(true)
  }

  /*
   * When URL param id updates, it sets new state of Sidebar panel
   */
  useEffect(() => {
    setas2ConfigSidebarOpen(Boolean(id))
  }, [id])

  /*
   * When query updates, it sets new state of as2ConnectionConfigs
   */
  useEffect(() => {
    setAs2ConnectionConfigs(
      (data?.as2ConnectionConfigs as IAs2ConnectionConfigConnection) || undefined
    )
  }, [data?.as2ConnectionConfigs])

  const closeDialogHandler = () => {
    setas2ConfigSidebarOpen(false)
    history.replace(`/connection-configs/${CONNECTION_CONFIG_TYPES.as2}`)
  }

  const { upsertAs2ConnectionConfig } = useAs2ConnectionConfig({
    displayUserFeedback,
  })

  const submitHandler = (data: IAs2ConnectionConfigSchema) => {
    const input: IUpsertAs2ConnectionConfigInput = {
      identifier: data?.identifier,
      outboundUrl: data?.outboundUrl,
      serverInfoCredentialsIdentifier: data?.serverInfoCredentialsIdentifier,
      transfixIdentifier: data?.transfixIdentifier,
    }

    if (data?.id) {
      input.id = data?.id
    }

    upsertAs2ConnectionConfig(input)
  }

  const classes = useAS2ConnectionConfigStyles()

  return (
    <div className={classes.root}>
      <Box pl={3}>
        {loading && <Spinner position='absolute' />}
        <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
        <Grid container direction='row' justifyContent='space-between' alignItems='center'>
          <Grid item xs={12} md='auto'>
            <Typography
              variant='h3'
              className={classes.text}
              color='primary'
              display='inline'
              component='div'>
              AS2 Connection Configurations
            </Typography>
          </Grid>
          <Grid item xs={12} md='auto'>
            <Button
              color='primary'
              size='small'
              data-test='add-as2-config-button'
              onClick={clickHandler}>
              Add New
            </Button>
          </Grid>
        </Grid>
        <Grid container direction='row' alignItems='center'>
          <Grid item xs={12} md='auto'>
            <Card className={classes.card}>
              <AS2ConnectionConfigTable
                as2ConnectionConfigs={as2ConnectionConfigs!}
                onEdit={submitHandler}
              />
            </Card>
          </Grid>
        </Grid>
        <AS2ConnectionConfigDetails
          isSidebarOpen={isSidebarOpen}
          onSidebarClose={closeDialogHandler}
          displayUserFeedback={displayUserFeedback}
        />
        <As2ConnectionConfigModal
          open={isOpen}
          onClose={closeHandler}
          onSubmit={submitHandler}
          title='Add new AS2 Connection Config'
        />
      </Box>
    </div>
  )
}
